import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../client'
import type { OcorrenciaParaRegistrar, PainelDoIad } from '../../types/iad'

/**
 * As chaves de cache do IAD.
 *
 * O painel é uma consulta só, e toda mutação devolve o painel inteiro já atualizado. Não é
 * economia de requisição: é que pontos, faixa, ocorrências e candidatas mudam JUNTOS — confirmar
 * uma candidata acrescenta 10 pontos, tira uma linha da lista de candidatas e pode cruzar a faixa
 * de glosa no mesmo clique. Quatro consultas separadas mostrariam esses quatro efeitos em quatro
 * instantes diferentes.
 */
const chaves = {
  tudo: () => ['iad'] as const,
  painel: (competencia: string) => ['iad', 'painel', competencia] as const,
}

/**
 * O painel do IAD de uma competência.
 *
 * @param competencia mês no formato `AAAA-MM`
 * @returns a consulta do painel
 */
export function usePainelDoIad(competencia: string) {
  return useQuery({
    queryKey: chaves.painel(competencia),
    queryFn: async () => (await api.get<PainelDoIad>(`/iad/${competencia}`)).data,
    enabled: competencia.length > 0,
  })
}

/** Invalida o módulo inteiro — e também o fechamento, porque o IAD entra na soma da fatura. */
function useAtualizarApuracao() {
  const cliente = useQueryClient()
  return () => {
    void cliente.invalidateQueries({ queryKey: chaves.tudo() })
    // O fechamento do mês exibe o IAD entre os seis indicadores. Sem esta linha, o fiscal registra
    // uma ocorrência aqui e a outra tela continua dizendo "não apurado" até ele recarregar.
    void cliente.invalidateQueries({ queryKey: ['chamados'] })
  }
}

/**
 * Registra uma ocorrência administrativa.
 *
 * @returns a mutação do registro
 */
export function useRegistrarOcorrencia() {
  const atualizar = useAtualizarApuracao()
  return useMutation({
    mutationFn: async ({ competencia, ...corpo }: OcorrenciaParaRegistrar) =>
      (await api.post<PainelDoIad>(`/iad/${competencia}/ocorrencias`, corpo)).data,
    onSuccess: atualizar,
  })
}

/**
 * Retira uma ocorrência da soma, sem apagá-la.
 *
 * @returns a mutação da revogação
 */
export function useRevogarOcorrencia() {
  const atualizar = useAtualizarApuracao()
  return useMutation({
    mutationFn: async ({ id, motivo }: { id: number; motivo: string }) =>
      (await api.post<PainelDoIad>(`/iad/ocorrencias/${id}/revogar`, { motivo })).data,
    onSuccess: atualizar,
  })
}

/**
 * Transcreve a manifestação da contratada.
 *
 * @returns a mutação da contestação
 */
export function useRegistrarContestacao() {
  const atualizar = useAtualizarApuracao()
  return useMutation({
    mutationFn: async ({ id, texto }: { id: number; texto: string }) =>
      (await api.post<PainelDoIad>(`/iad/ocorrencias/${id}/contestacao`, { texto })).data,
    onSuccess: atualizar,
  })
}

/**
 * Confirma uma candidata da conciliação: ela vira ocorrência do item 16.
 *
 * @returns a mutação da confirmação
 */
export function useConfirmarCandidata() {
  const atualizar = useAtualizarApuracao()
  return useMutation({
    mutationFn: async ({ competencia, gmudId, texto }: { competencia: string; gmudId: number; texto: string }) =>
      (await api.post<PainelDoIad>(`/iad/${competencia}/candidatas/${gmudId}/confirmar`, { texto })).data,
    onSuccess: atualizar,
  })
}

/**
 * Afasta uma candidata: não é infração, e a pergunta não volta.
 *
 * @returns a mutação do descarte
 */
export function useDescartarCandidata() {
  const atualizar = useAtualizarApuracao()
  return useMutation({
    mutationFn: async ({ competencia, gmudId, motivo }: { competencia: string; gmudId: number; motivo: string }) =>
      (await api.post<PainelDoIad>(`/iad/${competencia}/candidatas/${gmudId}/descartar`, { motivo })).data,
    onSuccess: atualizar,
  })
}

/**
 * Declara que o mês foi varrido — o ato que destrava a apuração do indicador.
 *
 * @returns a mutação da conferência
 */
export function useConferirOMes() {
  const atualizar = useAtualizarApuracao()
  return useMutation({
    mutationFn: async (competencia: string) => (await api.post<PainelDoIad>(`/iad/${competencia}/conferir`)).data,
    onSuccess: atualizar,
  })
}
